/**
 * Hook: DELETE /admin/super-admins/{id} — HARD-DELETE a super-admin (RBAC FE WS-D).
 *
 * Founder-directed semantics: this is a real deletion, not a soft revoke — after
 * it succeeds the user does not exist anywhere. The console renders this as
 * "Delete"; on success the row DISAPPEARS from the list.
 *
 * SA-host only; the affordance is hidden under impersonation (#8), but the
 * backend `require_not_impersonating` on the route is the real boundary
 * (`app/api/admin/sa_users.py`). Returns 204 on success.
 *
 * Distinct backend error codes (surfaced by the console, never collapsed):
 *   cannot_delete_self  400  — an SA cannot delete their own account. The console
 *                              also disables the self-row's button cosmetically,
 *                              but THIS 400 is the authoritative gate (#7 — the UI
 *                              may be wrong; the backend is not).
 *   not_found           404  — SA already gone (e.g. concurrent delete). The copy
 *                              is non-enumerating.
 *
 * On success (and on a 404 re-sync) the caller invalidates the SA-users list (#7)
 * so the deleted row disappears.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { saUsersKey } from "./saUserKeys";

interface DeleteSuperAdminVars {
  id: string;
}

export function useDeleteSuperAdmin(): UseMutationResult<
  void,
  ApiError,
  DeleteSuperAdminVars
> {
  const qc = useQueryClient();
  return useMutation<void, ApiError, DeleteSuperAdminVars>({
    mutationFn: async ({ id }) => {
      await apiClient.delete(`/admin/super-admins/${encodeURIComponent(id)}`);
    },
    onSuccess: () => {
      // #7 — re-sync the list with the backend after the write.
      qc.invalidateQueries({ queryKey: saUsersKey() });
    },
  });
}
